import { useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: ReactNode }[];
  'aria-label'?: string;
  className?: string;
  size?: 'sm' | 'md';
  /** Connects each tab to `${idPrefix}-${value}-panel`. */
  idPrefix?: string;
}

/**
 * Design-system segmented control / tabs (role="tablist"). Single source for
 * the pill-tab pattern in the app + the design-system gallery.
 */
export function SegmentedControl<T extends string>({ value, onChange, options, className, size = 'md', idPrefix, 'aria-label': ariaLabel }: SegmentedControlProps<T>) {
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn('flex bg-gray-100', size === 'sm' ? 'gap-1 rounded-md p-1' : 'gap-1 rounded-lg p-1', className)}>
      {options.map((o, index) => (
        <button
          key={String(o.value)}
          ref={node => { tabs.current[index] = node; }}
          id={idPrefix ? `${idPrefix}-${o.value}-tab` : undefined}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          aria-controls={idPrefix ? `${idPrefix}-${o.value}-panel` : undefined}
          tabIndex={value === o.value ? 0 : -1}
          onClick={() => onChange(o.value)}
          onKeyDown={event => {
            let next: number;
            if (event.key === 'ArrowRight') next = (index + 1) % options.length;
            else if (event.key === 'ArrowLeft') next = (index + options.length - 1) % options.length;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = options.length - 1;
            else return;
            event.preventDefault();
            onChange(options[next].value);
            tabs.current[next]?.focus();
          }}
          className={cn(
            'flex-1 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            size === 'sm' ? 'px-4 py-1 text-xs rounded' : 'px-3 py-2 text-sm rounded-md',
            value === o.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
          )}
        >
          {/* Text labels centre on their letters (.text-cap-trim, styles/index.css); the tab is a
              blockified flex item, so the span is block. Node labels render as given. */}
          {typeof o.label === 'string' || typeof o.label === 'number'
            ? <span className="text-cap-trim block">{o.label}</span>
            : o.label}
        </button>
      ))}
    </div>
  );
}
